import { type ReactElement, type ReactNode } from 'react';
import { Server } from 'lucide-react';
import { StatPill, type StatPillTone } from '../ui/StatPill';
import { ResourceRow } from '../ui/ResourceRow';
import { ChartBox } from '../ui/ChartBox';
import { recordActivity } from '../../lib/api';
import { clusterStore } from '../../stores/clusterStore';
import { ACCENT } from '../../styles/tokens';
import type { NodeInfo, TaskInfo } from '../../lib/types';

export interface NodePanelProps {
  item: NodeInfo;
  tasks: TaskInfo[];
  accent?: string;
}

function statusTone(status: NodeInfo['status']): StatPillTone {
  if (status === 'OK' || status === 'ready') return 'ok';
  if (status === 'down' || status === 'disconnected') return 'danger';
  return 'warn';
}

function statusLabel(status: NodeInfo['status']): string {
  if (status === 'OK' || status === 'ready') return 'SAUDÁVEL';
  if (status === 'down') return 'DOWN';
  if (status === 'disconnected') return 'OFFLINE';
  return 'ATENÇÃO';
}

function taskOk(status: string): boolean {
  return status === 'OK' || status === 'ready';
}

function PanelSection({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="flex-shrink-0">
      <div className="flex items-baseline justify-between px-4 pt-2.5 pb-1">
        <div className="font-mono text-[9.5px] font-bold tracking-[.08em] uppercase text-textMute">
          {title}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function loadSeries(cpu: number, seed: number): number[] {
  const base = Math.max(5, cpu);
  return Array.from({ length: 30 }, (_, i) => {
    const phase = (seed + i) * 0.4;
    return base + Math.sin(phase) * 12 + ((seed * (i + 1)) % 8);
  });
}

function formatGhz(mhz?: number): string {
  if (!mhz || mhz <= 0) return '—';
  return `${(mhz / 1000).toFixed(1)} GHz`;
}

function formatBytes(bytes: number): string {
  if (bytes <= 0) return '—';
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB'];
  let v = bytes;
  let u = 0;
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024;
    u += 1;
  }
  return `${v.toFixed(1)} ${units[u]}`;
}

export function NodePanel({ item, tasks, accent }: NodePanelProps) {
  const accentColor = accent ?? ACCENT;

  const nodeTasks = tasks
    .filter(t => t.nodeID === item.id)
    .sort((a, b) =>
      a.serviceName.localeCompare(b.serviceName, 'pt-BR', {
        numeric: true,
        sensitivity: 'base',
      })
    );
  const cpuPct = Math.max(0, Math.min(100, item.cpu));
  const seed = item.id.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  const loadData = loadSeries(cpuPct, seed);

  const cpuColor =
    cpuPct > 80 ? '#e11d48' : cpuPct > 60 ? '#d97706' : '#16a34a';

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="node-panel">
      <header
        className="flex-shrink-0 border-b border-borderSoft px-4 pt-3.5 pb-3"
        data-testid="node-panel-header"
      >
        <div className="flex items-center gap-2">
          <Server size={14} className="text-textMute" />
          <h2 className="m-0 flex-1 break-all font-mono text-sm font-bold tracking-tight text-text">
            {item.hostname}
          </h2>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <StatPill tone={statusTone(item.status)}>
            {statusLabel(item.status)}
          </StatPill>
          <span className="font-mono text-[10.5px] text-textMute">
            {item.ip || '—'}
          </span>
        </div>
      </header>

      <div className="flex-1 min-h-0 overflow-auto">
        {(item.cpuModel || item.cpuCores || item.cpuThreads) && (
          <PanelSection title="Hardware">
            <div
              className="px-4 pt-1 pb-3 font-mono text-[10.5px]"
              data-testid="node-panel-hardware"
            >
              <div className="break-words text-text" title={item.cpuModel}>
                {item.cpuModel || 'CPU —'}
              </div>
              <div className="mt-0.5 text-textMute">
                {item.cpuCores ?? '—'} cores · {item.cpuThreads ?? '—'} threads
                · {formatGhz(item.cpuMhz)}
              </div>
            </div>
          </PanelSection>
        )}
        <PanelSection title="Recursos · agora">
          <div className="px-4 pt-2.5 pb-3.5">
            <ResourceRow
              label="CPU"
              value={`${cpuPct}%`}
              sub="vCPU"
              pct={cpuPct}
              color={cpuColor}
            />
            <ResourceRow
              label="Memória"
              value={formatBytes(item.memoryBytes)}
              sub=""
              pct={0}
              color={accentColor}
            />
            <ResourceRow
              label="Máquinas"
              value={item.machines}
              sub={`de ${Math.max(1, item.machineAmount)}`}
              pct={(item.machines / Math.max(1, item.machineAmount)) * 100}
              color={accentColor}
            />
          </div>
        </PanelSection>

        <PanelSection title="Load · 24h">
          <div className="px-4 pt-1 pb-3.5">
            <ChartBox data={loadData} color={accentColor} h={46} />
          </div>
        </PanelSection>

        <PanelSection title={`Serviços rodando · ${nodeTasks.length}`}>
          <div data-testid="node-tasks">
            {nodeTasks.length === 0 && (
              <div className="px-4 py-4 text-[11px] text-textMute">
                Sem dados de serviços neste node.
              </div>
            )}
            {nodeTasks.length > 0 && (
              <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 px-4 py-1 text-[9px] font-semibold uppercase tracking-wide text-textMute">
                <span>serviço</span>
                <span>ip</span>
                <span>slot</span>
                <span className="text-right">status</span>
              </div>
            )}
            {nodeTasks.map(t => (
              <div
                key={t.taskID}
                className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-3 border-t border-borderSoft px-4 py-1.5 text-[10.5px]"
              >
                <span
                  data-testid="node-task-name"
                  className="truncate font-mono text-text"
                  title={t.serviceName}
                >
                  {t.serviceName}
                </span>
                <span className="font-mono text-textMute">
                  {t.ip ? (
                    <a
                      className="text-accent hover:underline"
                      href={`http://${t.ip}:${t.managerPort || 32500}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        recordActivity({
                          action: 'open-ip',
                          cluster: clusterStore.getState().activeSlug,
                          target: t.ip,
                          payload: { service: t.serviceName, host: item.hostname },
                        })
                      }
                    >
                      {t.ip}
                    </a>
                  ) : (
                    '—'
                  )}
                </span>
                <span className="whitespace-nowrap font-mono text-[9.5px] text-textMute">
                  slot {t.slot}
                </span>
                <span className="flex items-center justify-end gap-1 font-mono text-[9.5px] text-textMute">
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${taskOk(t.nodeStatus) ? 'bg-okDot' : 'bg-dangerDot'}`}
                  />
                  {taskOk(t.nodeStatus) ? 'ok' : 'down'}
                </span>
              </div>
            ))}
          </div>
        </PanelSection>
      </div>
    </div>
  );
}

NodePanel.displayName = 'NodePanel';

export default NodePanel;
